import {useCallback, useMemo, useRef} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {toast} from 'react-toastify';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';

/**
 * Form-friendly ReactQuill wrapper (accepts value/onChange so it can be a direct
 * <Form.Item> child). Adds text color + background (highlight) pickers, and when
 * `withImage` is set, replaces Quill's default base64 image button with a
 * handler that uploads the file to our S3 bucket and inserts the returned URL.
 *
 * Lives here rather than under the announcement flow because three features now
 * use it — announcements, subscription plan features, and the in-app
 * notification body. `adminPlans` was already importing it across features from
 * `pages/adminAnnouncement/fullScreenPopup/`.
 */

// The long-standing (misspelled) shared bucket for communication imagery.
export const COMMUNICATION_IMAGE_BUCKET = 'annoucement-images';

export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  withImage = false,
  className,
  bucket = COMMUNICATION_IMAGE_BUCKET,
}) => {
  const quillRef = useRef(null);

  const handleImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      try {
        const url = await UploadDocumentToBucket(file, () => {}, bucket);
        if (!url) {
          toast.error('Failed to upload image');
          return;
        }
        editor.insertEmbed(index, 'image', url, 'user');
        // Give inserted images a sensible default width so they don't fill the
        // whole editor / popup. Quill's image blot persists the width attribute
        // into the saved HTML (<img width="320">).
        editor.formatText(index, 1, 'width', '320', 'user');
        editor.setSelection(index + 1, 0);
      } catch (error) {
        toast.error('Failed to upload image');
      }
    };
    input.click();
  }, [bucket]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{header: [1, 2, 3, false]}],
          ['bold', 'italic', 'underline', 'strike'],
          [{color: []}, {background: []}],
          [{list: 'ordered'}, {list: 'bullet'}],
          withImage ? ['link', 'image'] : ['link'],
          ['clean'],
        ],
        ...(withImage && {handlers: {image: handleImage}}),
      },
    }),
    [withImage, handleImage],
  );

  const formats = useMemo(() => {
    const base = [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'list',
      'bullet',
      'link',
    ];
    return withImage ? [...base, 'image'] : base;
  }, [withImage]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      className={className}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      modules={modules}
      formats={formats}
    />
  );
};
